'use client';

import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { isSkewed, onSkew } from '@/lib/apiVersion';
import { reloadForStaleDeploy } from '@/lib/chunkReload';

declare global {
  interface Window {
    /** Set by useAgentChat while a turn is streaming — suppresses auto-reload. */
    __photoniumTurnActive?: boolean;
  }
}

// Matches the fallback stack in ErrorFallback.tsx's FONT_STACK, so the bar
// reads consistently even if it ever mounts before --font-sans resolves.
const FONT_STACK =
  "var(--font-sans, -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif)";

const styles: Record<string, CSSProperties> = {
  bar: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    padding: '8px 16px',
    textAlign: 'center',
    fontSize: 'var(--text-control)',
    fontFamily: FONT_STACK,
    background: 'var(--bg-secondary, #F9F8F5)',
    color: 'var(--text-primary, #1a1a1a)',
    borderTop: '1px solid var(--border-medium, #e5e7eb)',
  },
  button: {
    marginLeft: 6,
    border: 'none',
    background: 'none',
    padding: 0,
    font: 'inherit',
    color: 'var(--accent-primary, #0F425D)',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
};

export default function VersionSkewBar() {
  const t = useTranslations('common');
  const [skewed, setSkewed] = useState(isSkewed());
  const pathname = usePathname();
  const pathAtSkew = useRef<string | null>(null);

  useEffect(() => {
    // A skew flip landing between the initial render (which read isSkewed()
    // once) and this effect subscribing would otherwise be missed forever —
    // re-check the current value before/alongside subscribing.
    setSkewed(isSkewed());
    return onSkew(() => setSkewed(true));
  }, []);
  useEffect(() => {
    if (!skewed) return;
    pathAtSkew.current ??= pathname;
    // Auto-reload on the first navigation AFTER skew was detected — never
    // mid-turn, and never on the page the user is currently reading.
    if (pathname !== pathAtSkew.current && !window.__photoniumTurnActive) {
      reloadForStaleDeploy();
    }
  }, [skewed, pathname]);

  if (!skewed) return null;
  return (
    <div role="status" style={styles.bar}>
      {t('newVersionAvailable')}
      <button type="button" style={styles.button} onClick={() => window.location.reload()}>
        {t('reload')}
      </button>
    </div>
  );
}
